import { useEffect, useRef, type RefObject } from "react";
import { useMainScrollElement } from "@/app/context/MainScrollContext";

export type StepScrollAlignment = "start" | "end";

export const scrollStepIntoView = (
  main: HTMLElement,
  target: HTMLElement,
  alignment: StepScrollAlignment,
  behavior: ScrollBehavior,
) => {
  const maxTop = Math.max(0, main.scrollHeight - main.clientHeight);
  const targetTop = main.scrollTop + target.getBoundingClientRect().top - main.getBoundingClientRect().top;
  main.scrollTo({ top: alignment === "end" ? maxTop : Math.min(maxTop, Math.max(0, targetTop)), behavior });
};

export const useStepScroll = ({
  transitionKey,
  targetRef,
  enabled = true,
  alignment = "end",
  skipInitial = true,
}: {
  transitionKey: string | number | null;
  targetRef: RefObject<HTMLElement | null>;
  enabled?: boolean;
  alignment?: StepScrollAlignment;
  skipInitial?: boolean;
}) => {
  const main = useMainScrollElement();
  const observedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !main || !targetRef.current) return;
    if (!observedRef.current) {
      observedRef.current = true;
      if (skipInitial) return;
    }
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let frame = 0;
    let stopped = false;
    const scheduleScroll = () => {
      if (stopped) return;
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        if (!stopped && targetRef.current)
          scrollStepIntoView(main, targetRef.current, alignment, reduced ? "auto" : "smooth");
      });
    };
    // Imported games and analysis results can grow the page after the first frame.
    const observer = alignment === "end" ? new ResizeObserver(scheduleScroll) : null;
    const stop = () => {
      stopped = true;
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
    };
    const inputEvents = ["wheel", "touchstart", "pointerdown", "keydown"] as const;
    if (observer) {
      observer.observe(main.firstElementChild ?? targetRef.current);
      observer.observe(main);
      inputEvents.forEach((event) => main.addEventListener(event, stop, { passive: true }));
    }
    scheduleScroll();
    return () => {
      stop();
      inputEvents.forEach((event) => main.removeEventListener(event, stop));
    };
  }, [alignment, enabled, main, skipInitial, targetRef, transitionKey]);
};
